/**
 * Daily Deals Collection Grid Inserter
 * Dynamically inserts daily deals banner between product rows in collection pages
 */

class DailyDealsInjector {
  constructor() {
    this.dailyDealsSection = null
    this.productGrid = null
    this.shopifySectionWrapper = null
    this.config = {
      rowsMobile: 2,
      rowsTablet: 3,
      rowsDesktop: 4,
      maxRepeatsMobile: 1,
      maxRepeatsTablet: 1,
      maxRepeatsDesktop: 1,
    }
    this.injectedElements = []
    this.stylesConverted = false
    this.init()
  }

  init() {
    // Find the daily deals section
    this.dailyDealsSection = document.querySelector(
      '.section-daily-deals[data-daily-deals-inject="true"]',
    )

    if (!this.dailyDealsSection) {
      return // No daily deals section configured for insertion
    }

    // Find the product grid
    this.productGrid = document.querySelector("#product-grid")

    if (!this.productGrid) {
      return // No product grid found
    }

    // Read configuration from data attributes
    this.config = {
      rowsMobile: parseInt(
        this.dailyDealsSection.dataset.injectRowsMobile || "2",
      ),
      rowsTablet: parseInt(
        this.dailyDealsSection.dataset.injectRowsTablet || "3",
      ),
      rowsDesktop: parseInt(
        this.dailyDealsSection.dataset.injectRowsDesktop || "4",
      ),
      maxRepeatsMobile: parseInt(
        this.dailyDealsSection.dataset.maxRepeatsMobile || "1",
      ),
      maxRepeatsTablet: parseInt(
        this.dailyDealsSection.dataset.maxRepeatsTablet || "1",
      ),
      maxRepeatsDesktop: parseInt(
        this.dailyDealsSection.dataset.maxRepeatsDesktop || "1",
      ),
      targetCollection: this.dailyDealsSection.dataset.targetCollection || "",
    }

    // Check if we should display on this collection
    if (!this.shouldDisplayOnCurrentCollection()) {
      return
    }

    // Find the shopify section wrapper (parent element)
    this.shopifySectionWrapper = this.dailyDealsSection.closest(
      '[id^="shopify-section-"]',
    )

    // Perform initial injection (this will decide whether to hide original or show it)
    this.injectBanners()

    // Watch for viewport changes
    this.setupResizeHandler()

    // Watch for filter/sort changes using custom event
    this.setupFilterListener()

    // Watch for Load More clicks
    this.setupLoadMoreHandler()
  }

  getViewport() {
    const width = window.innerWidth
    if (width < 768) return "mobile"
    if (width < 1024) return "tablet"
    return "desktop"
  }

  getColumnsForViewport() {
    const viewport = this.getViewport()
    const grid = this.productGrid

    // Try to get columns from grid classes
    if (viewport === "mobile") {
      if (grid.classList.contains("grid--1-col-tablet-down")) return 1
      if (grid.classList.contains("grid--2-col-tablet-down")) return 2
      return 2 // default mobile
    }

    if (viewport === "tablet") {
      // For tablet, check your CSS - you have 3 columns based on your grid CSS
      return 3
    }

    // Desktop
    if (grid.classList.contains("grid--4-col-desktop")) return 4
    if (grid.classList.contains("grid--3-col-desktop")) return 3
    if (grid.classList.contains("grid--2-col-desktop")) return 2
    return 4 // default desktop
  }

  getRowFrequency() {
    const viewport = this.getViewport()
    if (viewport === "mobile") return this.config.rowsMobile
    if (viewport === "tablet") return this.config.rowsTablet
    return this.config.rowsDesktop
  }

  getMaxRepeatsForViewport() {
    const viewport = this.getViewport()
    if (viewport === "mobile") return this.config.maxRepeatsMobile
    if (viewport === "tablet") return this.config.maxRepeatsTablet
    return this.config.maxRepeatsDesktop
  }

  shouldDisplayOnCurrentCollection() {
    // If no target collection is set, display on all collections
    if (!this.config.targetCollection) {
      return true
    }

    // Get current collection handle from the page
    // Try multiple methods to get the collection handle
    let currentCollectionHandle = null

    // Method 1: From Shopify's collection object (if available)
    if (
      typeof Shopify !== "undefined" &&
      Shopify.routes &&
      Shopify.routes.root
    ) {
      const pathParts = window.location.pathname.split("/")
      const collectionsIndex = pathParts.indexOf("collections")
      if (collectionsIndex !== -1 && pathParts[collectionsIndex + 1]) {
        currentCollectionHandle = pathParts[collectionsIndex + 1].split("?")[0]
      }
    }

    // Method 2: From meta tag (if your theme adds it)
    if (!currentCollectionHandle) {
      const metaCollection = document.querySelector('meta[property="og:url"]')
      if (metaCollection) {
        const url = metaCollection.getAttribute("content")
        const match = url.match(/\/collections\/([^\/\?]+)/)
        if (match) {
          currentCollectionHandle = match[1]
        }
      }
    }

    // Method 3: From URL pathname
    if (!currentCollectionHandle) {
      const match = window.location.pathname.match(/\/collections\/([^\/\?]+)/)
      if (match) {
        currentCollectionHandle = match[1]
      }
    }

    if (!currentCollectionHandle) {
      console.warn("Daily Deals: Could not determine current collection")
      return false
    }

    // Compare handles
    const matches = currentCollectionHandle === this.config.targetCollection

    console.log(
      `Daily Deals: Current collection "${currentCollectionHandle}" ${
        matches ? "matches" : "does not match"
      } target "${this.config.targetCollection}"`,
    )

    return matches
  }

  injectBanners() {
    // Re-query product grid in case DOM was replaced (after filtering)
    this.productGrid = document.querySelector("#product-grid")

    if (!this.productGrid) {
      console.warn("Daily Deals: Product grid not found")
      return
    }

    // Re-query daily deals section in case DOM was replaced
    this.dailyDealsSection = document.querySelector(
      '.section-daily-deals[data-daily-deals-inject="true"]',
    )

    if (!this.dailyDealsSection) {
      console.warn("Daily Deals: Daily deals section not found")
      return
    }

    // Re-query shopify section wrapper
    this.shopifySectionWrapper = this.dailyDealsSection.closest(
      '[id^="shopify-section-"]',
    )

    // Remove any previously injected banners
    this.removeInjectedBanners()

    const products = Array.from(
      this.productGrid.querySelectorAll(
        ".grid__item:not(.daily-deals-injected)",
      ),
    )

    if (products.length === 0) {
      this.showOriginalSection()
      return
    }

    const columns = this.getColumnsForViewport()
    const rowFrequency = this.getRowFrequency()
    const maxRepeats = this.getMaxRepeatsForViewport()

    // Calculate how many rows we have based on the current column count
    const totalRows = Math.ceil(products.length / columns)

    // If we don't have enough rows for the "after every N rows" logic,
    // do not show the banner at all (no repeats, no original section).
    if (totalRows < rowFrequency) {
      console.log(
        `Daily Deals: Only ${totalRows} row(s) available, less than ${rowFrequency}. Hiding daily deals banner.`,
      )
      this.hideOriginalSection()
      return
    }

    // Convert ID-based styles to class-based styles (one-time operation)
    this.convertStylesToClassBased()

    // We have enough rows, hide original and insert clones strictly based on "after every N rows"
    this.hideOriginalSection()

    let insertionCount = 0

    // Loop row-by-row so that incomplete last rows still count as rows.
    for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
      if (insertionCount >= maxRepeats) {
        break
      }

      const rowNumber = rowIndex + 1

      // Only insert after rows that match the "after every N rows" setting
      if (rowNumber % rowFrequency !== 0) {
        continue
      }

      const rowStartIndex = rowIndex * columns
      const rowEndIndex = Math.min(
        rowStartIndex + columns - 1,
        products.length - 1,
      )

      const insertAfterProduct = products[rowEndIndex]

      if (!insertAfterProduct) {
        continue
      }

      // Skip if we've already inserted here
      if (
        insertAfterProduct.nextElementSibling?.classList.contains(
          "daily-deals-injected",
        )
      ) {
        continue
      }

      const clonedBanner = this.createBannerElement()

      // Skip if banner creation failed
      if (!clonedBanner) {
        continue
      }

      // Insert after the last product in the current row (even if the row is incomplete)
      insertAfterProduct.insertAdjacentElement("afterend", clonedBanner)

      this.injectedElements.push(clonedBanner)
      insertionCount++
    }

    console.log(
      `Daily Deals: Inserted ${insertionCount} banner(s) after every ${rowFrequency} row(s) (${this.getViewport()} view, ${totalRows} total rows, max repeats ${maxRepeats})`,
    )
  }

  hideOriginalSection() {
    if (this.shopifySectionWrapper) {
      this.shopifySectionWrapper.style.display = "none"
    } else if (this.dailyDealsSection) {
      this.dailyDealsSection.style.display = "none"
    }
  }

  showOriginalSection() {
    if (this.shopifySectionWrapper) {
      this.shopifySectionWrapper.style.display = "block"
    } else if (this.dailyDealsSection) {
      this.dailyDealsSection.style.display = "block"
    }
  }

  createBannerElement() {
    // Find and clone only the banner content div
    const bannerContent = this.dailyDealsSection.querySelector(
      ".image-with-text.isolate",
    )

    if (!bannerContent) {
      console.warn(
        "Daily Deals: Banner content (.image-with-text.isolate) not found",
      )
      return null
    }

    // Clone the banner content
    const clonedContent = bannerContent.cloneNode(true)

    // Get all classes from the original section
    const originalClasses = Array.from(this.dailyDealsSection.classList)

    // Get the section ID to filter out the padding class
    const sectionId = this.dailyDealsSection.id

    // Filter out the section-{id}-padding class pattern
    const classesToCopy = originalClasses.filter((className) => {
      // Exclude any class that matches section-*-padding pattern
      return !className.match(/^section-.*-padding$/)
    })

    // Create a wrapper list item with the class that styles will target
    const wrapper = document.createElement("li")
    wrapper.classList.add(
      "grid__item",
      "grid__item--full-width",
      "daily-deals-injected",
      ...classesToCopy, // Spread the copied classes
    )
    wrapper.appendChild(clonedContent)

    return wrapper
  }

  convertStylesToClassBased() {
    // Check if we've already inserted converted styles into head
    const existingStyles = document.head.querySelector(
      'style[data-daily-deals-injected-styles="true"]',
    )

    if (existingStyles || this.stylesConverted) {
      return // Already converted
    }

    if (!this.shopifySectionWrapper) {
      console.warn("Daily Deals: Shopify section wrapper not found")
      return
    }

    // Find the style tag (it's a sibling of the section, inside the wrapper)
    const originalStyleTag = this.shopifySectionWrapper.querySelector("style")

    if (!originalStyleTag) {
      console.warn("Daily Deals: Style tag not found")
      return
    }

    // Clone the style content (don't modify original)
    let styleContent = originalStyleTag.textContent

    // Get the section ID for replacement
    const sectionId = this.dailyDealsSection.id
    const shopifySectionId = this.shopifySectionWrapper.id

    // Helper function to escape special regex characters
    const escapeRegex = (str) => {
      return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    }

    // Escape the IDs for use in regex
    const escapedSectionId = escapeRegex(sectionId)
    const escapedShopifySectionId = escapeRegex(shopifySectionId)

    // Replace ID selectors with class selectors in the cloned content
    // #shopify-section-{id} → .daily-deals-injected
    const shopifySectionRegex = new RegExp(
      `#${escapedShopifySectionId}\\b`,
      "g",
    )
    styleContent = styleContent.replace(
      shopifySectionRegex,
      ".daily-deals-injected",
    )

    // #section-{id} → .daily-deals-injected
    const sectionRegex = new RegExp(`#${escapedSectionId}\\b`, "g")
    styleContent = styleContent.replace(sectionRegex, ".daily-deals-injected")

    // .section-{id}-padding → remove (we use .daily-deals-injected)
    const paddingClassRegex = new RegExp(
      `\\.${escapedSectionId}-padding\\b`,
      "g",
    )
    styleContent = styleContent.replace(paddingClassRegex, "")

    // Create a new style element for the cloned/converted styles
    const clonedStyleTag = document.createElement("style")
    clonedStyleTag.setAttribute("data-daily-deals-injected-styles", "true")
    clonedStyleTag.textContent = styleContent

    // Insert the cloned style tag into the head
    document.head.appendChild(clonedStyleTag)

    this.stylesConverted = true
  }

  removeInjectedBanners() {
    this.injectedElements.forEach((el) => {
      if (el && el.parentNode) {
        el.parentNode.removeChild(el)
      }
    })
    this.injectedElements = []

    // Also remove any orphaned injected elements
    const orphanedBanners = this.productGrid.querySelectorAll(
      ".daily-deals-injected",
    )
    orphanedBanners.forEach((el) => el.remove())
  }

  setupResizeHandler() {
    let resizeTimer
    let lastViewport = this.getViewport()

    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        const currentViewport = this.getViewport()
        // Only re-inject if viewport actually changed
        if (currentViewport !== lastViewport) {
          console.log(`Viewport changed: ${lastViewport} → ${currentViewport}`)
          lastViewport = currentViewport
          this.injectBanners()
        }
      }, 250) // Debounce resize events
    })
  }

  setupFilterListener() {
    // Listen to the custom filter:applied event from custom-filter-facets.js
    document.addEventListener("filter:applied", () => {
      console.log("Filter applied, reinitializing banners...")
      // Small delay to ensure products are fully rendered
      setTimeout(() => {
        this.injectBanners()
      }, 200)
    })
  }

  setupLoadMoreHandler() {
    document.addEventListener("more:products:loaded", () => {
      console.log("More products loaded, reinitializing banners...")
      // Wait for new products to be loaded, then re-inject
      setTimeout(() => {
        this.injectBanners()
      }, 200) // Adjust timing based on your load more implementation
    })
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new DailyDealsInjector()
  })
} else {
  new DailyDealsInjector()
}
